import Room from "../Room.js";
import Error from "../utils/Error.js";
import ConfirmationArea from "../ui/ConfirmationArea.js";

import {md5} from "hash";
import Dexie from "dexie";

import global from "../global.js";

import Channel from "./space/Channel.js";
import Context from "./space/Context.js";

import MediaIndex from "./space_index/MediaIndex.js";
import ContextIndex from "./space_index/ContextIndex.js";

import Hooks from "./space/Hooks.js";

import Mutex from "../utils/Mutex.js";

import {SPACE_DATABASE_VERSION, LOCAL_DESKTOP_NUMBER, LOCAL_CHANNEL_NUMBER} from "./constants.js";

const log = global.log.getLogger("f_mtac.storage.space");
log.setLevel(log.levels.WARN);

const data_store_name = ({space_index}) => `space/${space_index}`;
const data_store = ({space_index}) => {
    let name = data_store_name({space_index});
    let store = global.configuration[name];
    if (store !== undefined) {
        return store;
    }
    return null;
};

const database_name = ({space_index}) => `f_mtac.storage.${space_index}`;

const Space = function (space_index) {
    if (typeof space_index !== "number") {
        throw new Error.build.invalid({
            message: "wrong space_index type, shall be a number"
        });
    }
    var database = new Dexie(database_name({space_index}), {autoOpen: false});
    var context_lock = new Mutex();
    var context = null;
    var room = null;
    var media_index = new MediaIndex(this);
    var context_index = new ContextIndex(this);
    var created_date = new Date();
    var modified_date = new Date(created_date.valueOf());
    var channel = null;
    var space_data_store = null;
    var space_data = null;
    var channels = Array.from(Array(LOCAL_CHANNEL_NUMBER), () => null);
    var contexts = Array.from(Array(LOCAL_DESKTOP_NUMBER), () => null);
    var hooks = new Hooks();

    // is there a confirmation request on going
    let request_conflict_ongoing = false;

    database.version(SPACE_DATABASE_VERSION - 1).stores({
        medias: "local_id,media_hash"
    });
    database
        .version(SPACE_DATABASE_VERSION)
        .stores({
            medias: "local_id,media_hash"
        })
        .upgrade(async (tx) => {
            await tx
                .table("medias")
                .toCollection()
                .modify((_, ref) => {
                    delete ref.value;
                });
        });
    hooks.add("medias", this);
    hooks.add_group("objects");
    hooks.add_group("configurations");

    Object.defineProperty(this, "data", {
        set: () => {},
        get: () => space_data
    });
    Object.defineProperty(this, "name", {
        set: (name) => {
            space_data.name = name;
            global.event_bus.publish("storage.space.updated", {space: this});
        },
        get: () => space_data.name
    });
    Object.defineProperty(this, "modified", {
        set: (modified) => {
            space_data.modified = modified;
            modified_date.setTime(modified);
        },
        get: () => modified_date
    });
    Object.defineProperty(this, "created", {
        set: (created) => {
            space_data.created = created;
            created_date.setTime(created);
        },
        get: () => created_date
    });
    Object.defineProperty(this, "index", {
        set: () => {},
        get: () => space_index
    });
    Object.defineProperty(this, "room", {
        set: () => {},
        get: () => room
    });
    Object.defineProperty(this, "selected_channel", {
        set: () => {},
        get: () => channel
    });
    Object.defineProperty(this, "selected_context", {
        set: () => {},
        get: () => context
    });
    Object.defineProperty(this, "channel", {
        set: () => {},
        get: () => channels
    });
    Object.defineProperty(this, "context", {
        set: () => {},
        get: () => contexts
    });
    Object.defineProperty(this, "context_number", {
        set: () => {},
        get: () => LOCAL_DESKTOP_NUMBER
    });
    Object.defineProperty(this, "hooks", {
        set: () => {},
        get: () => hooks
    });
    Object.defineProperty(this, "last_context", {
        // Last selected context (desktop) index saved in 'master' indexedDB.
        set: (new_context_index) => {
            space_data.last_context = new_context_index;
            this.data_commit();
        },
        get: () => space_data.last_context
    });
    Object.defineProperty(this, "room_short_id", {
        // Last selected context (desktop) index saved in 'master' indexedDB.
        set: (room_short_id) => {
            space_data.room_short_id = room_short_id;
            this.data_commit();
        },
        get: () => space_data.room_short_id
    });
    this.data_commit = () => space_data_store.save();

    ////////////////
    // only one channel is supported for now
    channels[0] = new Channel(this, 0);
    channel = channels[0];
    ////////////////

    const change_context = async (old_context, new_context) => {
        log.debug("Space.change_context");
        var release = await context_lock.acquire();
        context = new_context;
        global.event_bus.publish("storage.context.changed", {old_context, new_context});
        if (old_context !== null) {
            let context_map_media = await old_context.map_media_id;
            if (context_map_media === null) {
                await this.delete_context({desktop_index: old_context.index});
            }
        }
        if (new_context) {
            // new_context can be null when closing space
            this.last_context = new_context.index;
        }
        release();
    };

    const change_channel = (old_channel, new_channel) => {
        log.debug("Space.change_channel");
        channel = new_channel;
        global.event_bus.publish("storage.channel.changed", {old_channel, new_channel});
    };

    const get_context_by_index = (desktop_index) => {
        log.debug("Space.get_context_by_index");
        if (desktop_index === undefined) {
            if (context !== null) {
                desktop_index = context.index;
            } else {
                throw new Error.build.noctx({
                    message: "no selected context",
                    data: {desktop_index}
                });
            }
        }
        if (desktop_index < 0 || desktop_index >= LOCAL_DESKTOP_NUMBER) {
            throw `desktop index shall be between 0 and ${LOCAL_DESKTOP_NUMBER}`;
        }
        return contexts[desktop_index];
    };

    const put_context_on_index = async (desktop_index) => {
        log.debug("Space.put_context_on_index");
        let new_context = new Context(this, desktop_index);
        await new_context.open();
        contexts[desktop_index] = new_context;
        hooks.add_subhooks(new_context.hooks);
        return new_context;
    };

    const share_channels = async () => {
        log.debug("Space.share_channels");
        for (let current_channel of channels) {
            if (current_channel === null) {
                continue;
            }
            await current_channel.init(room);
        }
    };

    global.event_bus.subscribe("subscribe.connected", async () => {
        log.debug("Space -> on subscribe.connected before context_index.pull");
        await context_index.pull();
    });

    const share_contexts = async () => {
        log.debug("Space.share_contexts");
        await global.server.subscribe.init(room);
        await space_data_store.init(room);
        let shared_contexts = [];
        for (let current_context of contexts) {
            if (current_context === null) {
                continue;
            }
            let current_room = room.duplicate();
            current_room.desktop_index = current_context.index;
            await current_context.share(current_room);
            shared_contexts.push(current_context);
        }
        if (shared_contexts.length === 0) {
            await this.update();
        }
    };

    this.share = async function (new_room) {
        log.debug("Space.share IN room =", {new_room});
        if (new_room === null) {
            room = null;
        } else {
            room = new_room.duplicate();
            room.desktop = null;
        }
        this.room_short_id = new_room.short_id;
        await media_index.init(room);
        await context_index.init(room);
        await share_channels();
        await share_contexts();
        await this.push();
        await this.pull();
    };

    const unshare_channels = async () => {
        log.debug("Space.unshare_channels");
        for (let current_channel of channels) {
            if (current_channel === null) {
                continue;
            }
            await current_channel.deinit();
        }
    };

    const unshare_contexts = async () => {
        log.debug("Space.unshare_contexts");
        await global.server.subscribe.deinit();
        await space_data_store.deinit();
        for (let current_context of contexts) {
            if (current_context === null) {
                continue;
            }
            await current_context.unshare();
        }
    };

    this.unshare = async function () {
        log.debug("Space.unshare");
        this.room_short_id = "";
        room = null;
        media_index.deinit();
        context_index.deinit();
        await unshare_channels();
        await unshare_contexts();
    };

    this.pull = async () => {
        log.debug("Space.pull");
        for (let current_channel of channels) {
            if (current_channel === null) {
                continue;
            }
            await current_channel.pull();
        }
        for (let current_context of contexts) {
            if (current_context === null) {
                continue;
            }
            await current_context.pull();
        }
        await media_index.pull();
    };

    this.check_room = async () => {
        log.debug("Space.check_room");
        return context_index.check_room();
    };

    this.push = async () => {
        log.debug("Space.push in");

        if (request_conflict_ongoing) {
            // don't push, leave
            log.debug("Space.push conflict on going, go out");
            return;
        }

        for (let current_channel of channels) {
            if (current_channel === null) {
                continue;
            }
            await current_channel.push();
        }

        let request_conflict_confirmation = undefined;
        let request_conflict = async () => {
            log.debug("Space.request_conflict ConfirmationArea.interact");
            let confirmation = await ConfirmationArea.interact({
                title: global.internationalization.text("collaboration.Conflict"),
                tip1: global.internationalization.text("collaboration.Local-crisis-cannot-be-synchronized"),
                tip2: global.internationalization.text("collaboration.Accept-server-crisis"),
                accept: true,
                backdrop: "static"
            });
            // confirmation = true : get server state
            // confirmation = false : leave the sharing
            if (!confirmation) {
                await global.storage.unshare({});
            }
            log.debug(`Space.push request_conflict return ${confirmation}`);
            return confirmation;
        };

        try {
            log.debug("Space.push before context_index.push");
            await context_index.push();
            log.debug("Space.push after context_index.push");
        } catch (error) {
            log.debug(`Space.push error catched request_conflict_ongoing = ${request_conflict_ongoing}`);
            switch (error.status) {
                case Error.status.conflict:
                    // check if there is a confirmation request ongoing
                    if (request_conflict_ongoing === true) {
                        break;
                    }
                    request_conflict_ongoing = true;
                    request_conflict_confirmation = await request_conflict();
                    //request_conflict_ongoing = false;
                    log.debug(`Space.push request_conflict confirmation = ${request_conflict_confirmation}`);
                    if (!request_conflict_confirmation) {
                        request_conflict_ongoing = false;
                        // leave the sharing
                        return;
                    }
                    // re-get all from sharing
                    log.debug("Space.push conflicts", error.data.conflicts);
                    for (let conflict of error.data.conflicts) {
                        log.debug("Space.push conflict", conflict);
                        let desktop_index = Room.desktop_id_to_desktop_index(conflict.desktop_id);
                        await this.reset_context({desktop_index});
                        log.debug("request_conflict_ongoing ", request_conflict_ongoing);
                        log.debug("recuperation synchro serveur demarrée");
                        await contexts[desktop_index].pull();
                        // attendre la fin de la recuperation serveur avant de mettre à jour la variable
                        log.debug("recuperation synchro serveur terminée");
                        log.debug("request_conflict_ongoing ", request_conflict_ongoing);
                        request_conflict_ongoing = false;
                    }
                    break;
                default:
                    log.error("unknown error during context push", error);
                    return;
            }
        }

        log.debug("Space.push before loop on contexts");
        // conflicted_context stores the list of conflicted contexts
        var conflicted_context = [];
        for (let current_context of contexts) {
            if (current_context === null) {
                continue;
            }
            try {
                log.debug("Space.push before current_context.push");
                await current_context.push();
                log.debug("Space.push after current_context.push");
            } catch (error) {
                log.debug("Space.push error during current_context.push");
                switch (error.status) {
                    case Error.status.conflict:
                        // "push" in the array (not on the server)
                        conflicted_context.push(current_context);
                        break;
                    default:
                        log.error("unknown error during context push", error);
                        return;
                }
            }
        }
        log.debug("Space.push before media_index.push");
        await media_index.push();
        log.debug(`Space.push conflicted_context.length = ${conflicted_context.length}`);
        if (conflicted_context.length > 0) {
            log.debug(`Space.push error request_conflict_ongoing = ${request_conflict_ongoing}`);
            if (request_conflict_confirmation === undefined) {
                if (request_conflict_ongoing === true) {
                    return;
                }
                request_conflict_ongoing = true;
                request_conflict_confirmation = await request_conflict();
                //request_conflict_ongoing = false;
            }
            log.debug(`Space.push request_conflict_confirmation = ${request_conflict_confirmation}`);
            if (!request_conflict_confirmation) {
                request_conflict_ongoing = false;
                return;
            }
        }
        log.debug("Space.push before revert");
        for (let current_context of conflicted_context) {
            await current_context.revert();
            log.debug("recuperation synchro serveur terminée");
            log.debug("request_conflict_ongoing ", request_conflict_ongoing);
            request_conflict_ongoing = false;
        }
        log.debug("Space.push out");
    };

    this.is_empty = async () => {
        for (let context of contexts) {
            if (await this.context_is_empty(context)) {
                continue;
            }
            return false;
        }
        for (let channel of channels) {
            if (await this.channel_is_empty(channel)) {
                continue;
            }
            return false;
        }
        return true;
    };

    this.context_is_empty = async (context) => {
        if (context === null) {
            return true;
        }
        return context.is_empty();
    };

    this.channel_is_empty = async (channel) => {
        if (channel === null) {
            return true;
        }
        return channel.is_empty();
    };

    this.has_context = async function () {
        for (let context of contexts) {
            if (!(await this.context_is_empty(context))) {
                return true;
            }
        }
        return false;
    };

    this.has_empty = async function () {
        for (let context of contexts) {
            if (await this.context_is_empty(context)) {
                return true;
            }
        }
        return false;
    };

    this.create_channel = async ({channel_index}) => {
        let new_channel = new Channel(this, channel_index);
        channels[channel_index] = new_channel;
        return new_channel;
    };

    this.create_context = async ({desktop_index}) => {
        log.debug(`Space.create_context desktop_index = ${desktop_index}`);
        let new_context = get_context_by_index(desktop_index);
        if (new_context !== null) {
            return {new_context};
        }
        new_context = await put_context_on_index(desktop_index);
        if (room !== null && new_context.room === null) {
            let desktop_room = room.duplicate();
            desktop_room.desktop_index = new_context.index;
            await new_context.share(desktop_room);
            /*
            // not ready for now
            context_index.commit({
                desktop_id: Room.desktop_index_to_desktop_id(new_context.index),
                status: "create"
            });
            await context_index.push();
            */
        }
        global.event_bus.publish("storage.context.created", {desktop_index, new_context});
        return {new_context};
    };

    this.open_context = async ({desktop_index}) => {
        log.debug(`Space.open_context desktop_index = ${desktop_index}`);
        let old_context = context;
        let {new_context} = await this.create_context({desktop_index});
        this.modified = Date.now();
        if (old_context !== new_context) {
            await change_context(old_context, new_context);
        }
        return {old_context, new_context};
    };

    this.close_context = async ({desktop_index}) => {
        log.debug(`Space.close_context desktop_index = ${desktop_index}`);
        let old_context = get_context_by_index(desktop_index);
        if (old_context === null) {
            return;
        }
        if (old_context === context) {
            await change_context(old_context, null);
        }
        hooks.delete_subhooks(old_context.hooks);
        contexts[old_context.index] = null;
        await old_context.close();
        global.event_bus.publish("storage.context.closed", {desktop_index, old_context});
    };

    this.delete_context = async ({desktop_index}) => {
        log.debug(`Space.delete_context desktop_index = ${desktop_index}`);
        let old_context = get_context_by_index(desktop_index);
        if (old_context === null) {
            return;
        }
        if (old_context === context) {
            await change_context(old_context, null);
        }
        hooks.delete_subhooks(old_context.hooks);
        contexts[old_context.index] = null;
        await old_context.delete();
        global.event_bus.publish("storage.context.deleted", {desktop_index, old_context});
    };

    this.delete_context_safe = async ({desktop_index}) => {
        log.debug(`Space.delete_context_safe desktop_index = ${desktop_index}`);
        let current_desktop_index = null;
        if (context !== null) {
            current_desktop_index = context.index;
        }
        await this.delete_context({desktop_index});
        if (current_desktop_index === desktop_index) {
            await this.create_context({desktop_index});
            await this.open_context({desktop_index});
        }
    };

    this.reset_context = async ({desktop_index}) => {
        log.debug(`Space.reset_context desktop_index = ${desktop_index}`);
        let current_desktop_index = null;
        if (context !== null) {
            current_desktop_index = context.index;
        }
        // delete media if needed (not deleted by delete_object_in_database when receiving room deletion notif)
        await global.scene.objects.tx_delete_all_media("resources");

        await this.delete_context({desktop_index});
        await this.create_context({desktop_index});
        if (current_desktop_index === desktop_index) {
            await this.open_context({desktop_index});
        }
    };

    this.delete_context_remote = async ({desktop_index}) => {
        log.debug(`Space.delete_context_remote desktop_index = ${desktop_index}`);
        let old_context = get_context_by_index(desktop_index);
        if (old_context === null) {
            return;
        }
        if (old_context.room === null) {
            return;
        }
        let global_hash = old_context.remote_global_hash;
        let desktop_id = Room.desktop_index_to_desktop_id(desktop_index);
        context_index.commit({desktop_id, status: "delete", global_hash});
        await this.push();
    };

    this.delete_all_context = async () => {
        log.debug("Space.delete_all_context");
        for (let desktop_index = 0; desktop_index < LOCAL_DESKTOP_NUMBER; desktop_index++) {
            await this.delete_context({desktop_index});
        }
    };

    this.delete_agnet_resources_in_all_context = async () => {
        log.debug("Space.delete_agnet_resources_in_all_context");
        for (let desktop_index = 0; desktop_index < LOCAL_DESKTOP_NUMBER; desktop_index++) {
            if (contexts[desktop_index] !== null) {
                let objects = await contexts[desktop_index].object_retrieve({}).toArray();
                for (let data of objects) {
                    if (data.object_data.layer_name === "resources") {
                        if (data.object_data.attributes.dataMsisdn !== undefined) {
                            contexts[desktop_index].object_agnet_delete({local_id: data.local_id});
                        }
                    }
                }
            }
        }
    };

    this.delete_agnet_resources_in_context = async ({desktop_index}) => {
        log.debug(`Space.delete_agnet_resources_in_context desktop_index = ${desktop_index}`);
        if (contexts[desktop_index] !== null) {
            let objects = await contexts[desktop_index].object_retrieve({}).toArray();
            for (let data of objects) {
                if (data.object_data.layer_name === "resources") {
                    if (data.object_data.attributes.dataMsisdn !== undefined) {
                        contexts[desktop_index].object_agnet_delete({local_id: data.local_id});
                    }
                }
            }
        }
    };

    this.update_agnet_resources_in_other_context = async (desktop_id, msisdn, object_data) => {
        log.debug(`Space.update_agnet_resources_in_other_context desktop_id = ${desktop_id}`);
        for (let desktop_index = 0; desktop_index < LOCAL_DESKTOP_NUMBER; desktop_index++) {
            if (contexts[desktop_index] !== null && desktop_index !== desktop_id) {
                let objects = await contexts[desktop_index].object_retrieve({}).toArray();
                for (let data of objects) {
                    if (data.object_data.layer_name === "resources") {
                        if (data.object_data.attributes.dataMsisdn !== undefined && data.object_data.attributes.dataMsisdn === msisdn) {
                            // For the moment only resource color can be updated for Agnet resource
                            data.object_data.children[0] = object_data.children[0];
                            contexts[desktop_index].update_object_in_database({local_id: data.local_id, object_data: data.object_data, object_hash: data.old_hash});
                        }
                    }
                }
            }
        }
    };

    /**
     * @brief Get the common grid  data of the first desktop different from the selected one
     * @return {str} the first dataCommonGrid info found or undefined if not found
     */
    this.get_grid_common_data = async () => {
        log.debug("Space.get_grid_common_data");

        for (let desktop_index = 0; desktop_index < LOCAL_DESKTOP_NUMBER; desktop_index++) {
            if (contexts[desktop_index] !== null && desktop_index !== this.selected_context.index) {
                let objects = await contexts[desktop_index].object_retrieve({}).toArray();
                for (let data of objects) {
                    if (data.object_data.layer_name === "media") {
                        if (data.object_data.attributes.dataCommonGrid !== undefined) {
                            log.debug("Space.get_grid_common_data found");
                            return data.object_data.attributes.dataCommonGrid;
                        }
                    }
                }
            }
        }
        log.debug("Space.get_grid_common_data: not found");
    };

    /*
     * @brief Apply the given grid data to all desktops of the space
     */
    this.update_all_grids = async (common_grid) => {
        //let _data = JSON.parse(JSON.stringify(grid_data));
        log.debug("Space.update_all_grids ", {common_grid});

        // loop on all desktops
        for (let desktop_index = 0; desktop_index < LOCAL_DESKTOP_NUMBER; desktop_index++) {
            if (contexts[desktop_index] !== null && desktop_index !== this.selected_context.index) {
                let objects = await contexts[desktop_index].object_retrieve({}).toArray();
                // oop on all objects
                for (let data of objects) {
                    if (data.object_data.layer_name === "media") {
                        if (data.object_data.attributes.dataGrid !== undefined) {
                            // set new value and commit
                            //let old_color = data.object_data.attributes.dataGrid.grid.color;

                            log.debug(`Space.update_all_grids done for desktop ${desktop_index}`);
                            data.object_data.attributes.dataCommonGrid = common_grid;
                            //data.object_data.attributes.dataGrid.grid.color = old_color;
                            contexts[desktop_index].update_object_in_database({
                                local_id: data.local_id,
                                object_data: JSON.stringify(data.object_data),
                                object_hash: data.old_hash
                            });
                        }
                    }
                }
            }
        }
    };

    this.delete_channel = async ({channel_index}) => {
        var old_channel = channels[channel_index];
        if (old_channel === null) {
            return;
        }
        if (old_channel === channel) {
            change_channel(old_channel, null);
        }
        channels[channel_index] = null;
        await old_channel.delete();
    };

    this.delete_all_channels = async () => {
        for (let channel_index = 0; channel_index < LOCAL_CHANNEL_NUMBER; channel_index++) {
            this.delete_channel({channel_index});
        }
    };

    this.delete_all_medias = async () => {
        let medias = database.medias.toCollection();
        return medias
            .modify((media, ref) => {
                delete ref.value;
            })
            .catch((error) => {
                log.error("media delete error:", error);
                throw error;
            });
    };

    this.context_exists = async function ({desktop_index}) {
        if (await global.storage.database_exists(Context.database_name({space_index, desktop_index}))) {
            return true;
        }
        return false;
    };

    this.open = async function () {
        log.debug("Space.open created_date", created_date);
        space_data_store = await global.configuration.add_store(data_store_name({space_index}), {
            name: "",
            created: created_date.valueOf(),
            modified: modified_date.valueOf(),
            last_context: 0,
            room_short_id: ""
        });
        space_data = space_data_store.export();
        await database.open();
        hooks.bind(database);
        await media_index.init(null);
        await context_index.init(null);
        // opening all existing desktops
        for (let index = 0; index < LOCAL_DESKTOP_NUMBER; index++) {
            // we are checking here if the context exists because
            // we don't want to allocate contexts which has not been created yet
            if (await this.context_exists({desktop_index: index})) {
                await this.create_context({desktop_index: index});
            }
        }
        // Agnet : do some cleanings if some Agnet resources remain after app start
        this.delete_agnet_resources_in_all_context();
        return this;
    };

    this.delete = async function () {
        log.debug("Space.delete", {context});
        var old_context = context;
        if (old_context !== null) {
            await change_context(old_context, null);
        }
        hooks.unbind(database);
        await this.delete_all_channels();
        await this.delete_all_context();
        await space_data_store.delete();
        await database.delete();
    };

    this.close_space = async () => {
        log.debug("Space.close_space", {context});
        var old_context = context;
        if (old_context === null) {
            return;
        }
        if (old_context === context) {
            await change_context(old_context, null);
        }
    };

    this.close = async () => {
        hooks.unbind(database);
        await database.close();
    };

    this.duplicate = async function (new_space_index) {
        if (this.index === new_space_index) {
            return;
        }
        const new_database = new Dexie(Space.database_name({space_index: new_space_index}));
        const schema = database.tables.reduce((result, table) => {
            result[table.name] = [table.schema.primKey]
                .concat(table.schema.indexes)
                .map((indexSpec) => indexSpec.src)
                .toString();
            return result;
        }, {});
        new_database.version(database.verno).stores(schema);

        await database.tables.reduce((prev, table) => {
            return prev.then(() => table.toArray()).then((rows) => new_database.table(table.name).bulkPut(rows));
        }, Promise.resolve());

        let new_space_data_store = data_store_name({space_index: new_space_index});
        let new_space_data = Dexie.deepClone(this.data);
        await global.configuration.add_store(new_space_data_store, new_space_data);
        await new_database.close();
    };

    this.update = async () => {
        log.debug("Space.update");
        await context_index.pull();
    };

    this.on_object_server_update = async (desktop_id, object) => {
        log.debug("Space.on_object_server_update", {desktop_id, object});
        let desktop_index = Room.desktop_id_to_desktop_index(desktop_id);
        let current_context = contexts[desktop_index];
        if (current_context === null) {
            ({new_context: current_context} = await this.create_context({desktop_index}));
        }

        //MJOMJO
        await current_context.object_handle_notify(object);
        // AGPOC-6408/6666: SITAC status not updated after a modification by another user
        global.event_bus.publish("storage.context.updated", {desktop_index});
    };

    this.on_configuration_server_update = async (desktop_id, configuration) => {
        log.debug("Space.on_configuration_server_update", {desktop_id, configuration});
        let desktop_index = Room.desktop_id_to_desktop_index(desktop_id);
        let current_context = contexts[desktop_index];
        if (current_context === null) {
            ({new_context: current_context} = await this.create_context({desktop_index}));
        }
        await current_context.configuration_handle_notify(configuration);
    };

    this.on_room_server_update = async (desktop_id, {status}) => {
        log.debug("Space.on_room_server_update", {desktop_id, status});
        if (desktop_id === undefined) {
            return;
        }
        let desktop_index = Room.desktop_id_to_desktop_index(desktop_id);
        switch (status) {
            case "reseting":
                await this.reset_context({desktop_index});
                break;
            default:
                log.error(`unknown room status ${status}`);
                break;
        }
    };

    this.on_message_server_update = async (channel_id, message) => {
        log.debug("Space.on_message_server_update", {channel_id, message});
        channel_id = 0; // always 0 for now
        if (message === null) {
            channels[channel_id].message_delete_all({rx_time: 0});
            return;
        }
        if (message.sender_login === global.configuration.user.login) {
            return;
        }
        channels[channel_id].message_create(message);
    };

    this.media_create = function ({media_hash, media_name, media_info, media_data}) {
        log.debug("Space.media_create ", {media_hash, media_name, media_info, media_data});
        let new_media = {},
            local_media_hash;
        if (media_data !== undefined) {
            new_media.media_data = media_data;
        } else {
            throw "media data is required";
        }
        if (media_name !== undefined) {
            new_media.media_name = media_name;
        }
        if (media_info !== undefined) {
            new_media.media_info = media_info;
        }
        if (media_hash !== undefined) {
            new_media.media_hash = media_hash;
            new_media.local_id = "media-" + media_hash;
            local_media_hash = Promise.resolve(media_hash);
        } else {
            local_media_hash = new Promise((resolve) => {
                let fileReader = new FileReader();
                fileReader.onload = function (event) {
                    resolve(event.target.result);
                };
                fileReader.readAsArrayBuffer(media_data);
            })
                .then((buffer) => {
                    new_media.media_hash = md5.buffer_hash(buffer);
                    new_media.local_id = "media-" + new_media.media_hash;
                    new_media.media_name = media_name;
                    new_media.media_info = media_info;
                })
                .then(() => {
                    media_index.commit(new_media);
                    return new_media.media_hash;
                });
        }
        return local_media_hash
            .then(() => {
                return database.medias.put(new_media).catch((error) => {
                    log.error("media create error: ", error);
                    throw error;
                });
            })
            .then(() => {
                if (media_hash === undefined) {
                    log.debug("Space.media_create avant media_index.push");
                    return media_index.push();
                }
            })
            .catch((error) => {
                throw error;
            })
            .then(() => new_media);
    };

    this.media_delete = async function ({local_id}) {
        log.debug("Space.media_delete ", local_id);
        let media_used = 0;
        for (let current_context of contexts) {
            if (current_context === null) {
                continue;
            }
            if (current_context === this.selected_context) {
                continue;
            }
            let media_objects = current_context.objects.where("object_data.attributes.dataMedia").equals(local_id);
            await media_objects.each(() => {
                media_used++;
            });

            // search also if media cache for gis map exits in other contexts
            // TODO : make it generic!
            if (current_context.type == "gis") {
                media_objects = current_context.objects.where("object_data.attributes.dataMediaLayer1").equals(local_id);
                await media_objects.each(() => {
                    media_used++;
                });
                media_objects = current_context.objects.where("object_data.attributes.dataMediaLayer2").equals(local_id);
                await media_objects.each(() => {
                    media_used++;
                });
                media_objects = current_context.objects.where("object_data.attributes.dataMediaLayer3").equals(local_id);
                await media_objects.each(() => {
                    media_used++;
                });
            }
        }
        if (media_used > 0) {
            log.warn(`discarding media deletion of ${local_id}`);
            return;
        }
        log.warn(`deleting media ${local_id}`);

        let old_media = {};
        let medias = database.medias;
        if (local_id !== undefined) {
            medias = medias.where("local_id").equals(local_id);
        } else {
            throw "local id shall be defined";
        }

        return medias
            .modify((media, ref) => {
                Object.assign(old_media, media);
                delete ref.value;
            })
            .catch((error) => {
                log.error("media delete error: ", error);
                throw error;
            })
            .then(() => {
                for (let current_context of contexts) {
                    if (current_context === null) {
                        continue;
                    }
                }
                media_index.commit({
                    local_id,
                    media_hash: old_media.media_hash,
                    media_data: null
                });
                return media_index.push();
            })
            .catch((error) => {
                switch (error.status) {
                    case Error.status.netdown:
                    case Error.status.hostdown:
                        return;
                    default:
                        throw error;
                }
            })
            .then(() => old_media);
    };

    this.media_local_delete = async function ({local_id}) {
        let media_used = 0;
        for (let current_context of contexts) {
            if (current_context === null) {
                continue;
            }
            if (current_context === this.selected_context) {
                continue;
            }
            let media_objects = current_context.objects.where("object_data.attributes.dataMedia").equals(local_id);
            await media_objects.each(() => {
                media_used++;
            });
        }
        if (media_used > 0) {
            log.warn(`discarding media deletion of ${local_id}`);
            return;
        }
        log.warn(`deleting media ${local_id}`);

        let old_media = {};
        let medias = database.medias;
        if (local_id !== undefined) {
            medias = medias.where("local_id").equals(local_id);
        } else {
            throw "local id shall be defined";
        }

        return medias
            .modify((media, ref) => {
                Object.assign(old_media, media);
                delete ref.value;
            })
            .catch((error) => {
                log.error("media delete error: ", error);
                throw error;
            })
            .then(() => old_media);
    };

    this.media_retrieve = async function ({local_id}) {
        if (local_id === undefined) {
            return database.medias.toCollection();
        }
        let media_data = await database.medias.get(local_id);
        if (media_data !== undefined) {
            return media_data;
        }
        await media_index.pull();
        return database.medias.get(local_id);
    };

    this.on = (table_name, event_name, handler) => {
        hooks.on(table_name, event_name, handler);
    };

    this.off = (table_name, event_name, handler) => {
        hooks.off(table_name, event_name, handler);
    };

    /**
     * @brief: find the objects taht are linked to the given user (=device) in the whole space
     * @params device, input = the uuid of the device
     * @returns a list of objects
     **/
    this.find_objects_linked_to_user = async (device) => {
        log.debug(`Space.find_objects_linked_to_user in device = ${device}`);
        let result = [];

        try {
            for (let context of contexts) {
                if (context === null) {
                    continue;
                }

                let object = await context.find_object_by_param("object_data.attributes.dataUser", device);
                if (object != null) {
                    // append to array
                    result.push(object);
                }
            }
        } catch (error) {
            log.error("Space.find_objects_linked_to_user Exception", error);
        }

        log.debug("Space.find_objects_linked_to_user -> out", result);
        return result;
    };
};
Space.data_store_name = data_store_name;
Space.data_store = data_store;
Space.database_name = database_name;

export default Space;
